import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { httpsCallable } from 'firebase/functions';
import { functions } from '../lib/firebase';
import { useUser } from '../auth/AuthProvider';

export interface MirrorEventRow {
  id: string;
  googleEventId: string;
  summary: string;
  start: { _seconds?: number; seconds?: number; toDate?: () => Date };
  end: { _seconds?: number; seconds?: number; toDate?: () => Date };
  bookedByAi?: boolean;
  matchedEventTypeId?: string | null;
  callId?: string | null;
  /** Booking-slot ID (`bookingSlots[].id`). null = legacy / manual entry,
   *  rendered in the implicit "Standard" column. */
  slotId?: string | null;
  calendarProvider?: string | null;
  verified?: boolean;
  actualDurationMinutes?: number | null;
  effectivenessScore?: number | null;
  notes?: string;
}

const QK = (fromMs?: number, toMs?: number) => ['mirror', fromMs ?? 0, toMs ?? 0] as const;

export function useMirrorEvents(range?: { fromMs?: number; toMs?: number }) {
  const { user } = useUser();
  return useQuery<MirrorEventRow[]>({
    queryKey: QK(range?.fromMs, range?.toMs),
    enabled: !!user,
    staleTime: 30_000,
    queryFn: async () => {
      const fn = httpsCallable<{ fromMs?: number; toMs?: number }, { items: MirrorEventRow[] }>(
        functions,
        'listMirrorEvents',
      );
      const r = await fn(range ?? {});
      return r.data.items;
    },
  });
}

export interface MirrorVerificationInput {
  eventId: string;
  verified: boolean;
  actualDurationMinutes?: number;
  effectivenessScore?: number;
  notes?: string;
}

export function useUpdateMirrorVerification() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: MirrorVerificationInput) => {
      const fn = httpsCallable<MirrorVerificationInput, { ok: true }>(
        functions,
        'updateMirrorVerification',
      );
      await fn(input);
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['mirror'] }),
  });
}

export function tsToDate(v: MirrorEventRow['start'] | undefined | null): Date | null {
  if (!v) return null;
  if (typeof v === 'object' && 'toDate' in v && typeof v.toDate === 'function') return v.toDate();
  const seconds = v._seconds ?? v.seconds;
  if (typeof seconds === 'number') return new Date(seconds * 1000);
  return null;
}
